import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db } from '../../firebase';
import { auth } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { User, CheckCircle, AlertCircle } from 'lucide-react';


const NG_WORDS = [
  '死ね','殺す','バカ','馬鹿','アホ','クズ','ゴミ','死','キモい','うざい','消えろ',
  'fuck','shit','bitch','asshole','bastard','damn','crap',
];
const containsNgWord = (text) =>
  NG_WORDS.some(w => text.toLowerCase().includes(w.toLowerCase()));

const NicknameSetupModal = ({ onComplete }) => {
  const { user } = useAuth();
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [skipped, setSkipped] = useState(false);

  const validate = (val) => {
    if (!val.trim()) return 'ニックネームを入力してください';
    if (val.trim().length < 2) return '2文字以上で入力してください';
    if (val.trim().length > 12) return '12文字以内で入力してください';
    if (containsNgWord(val)) return '使用できない言葉が含まれています';
    return '';
  };

  const handleSave = async () => {
    const err = validate(nickname);
    if (err) { setError(err); return; }
    setSaving(true);
    try {
      const trimmed = nickname.trim();
      // Firestore: nickname + username + displayName 全フィールドに統一保存
      await updateDoc(doc(db, 'users', user.uid), {
        nickname:    trimmed,
        username:    trimmed,
        displayName: trimmed,
        nicknameSetAt: serverTimestamp(),
      });
      // Firebase Auth の displayName も更新
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: trimmed });
      }
      onComplete(trimmed);
    } catch (e) {
      console.error(e);
      setError('保存に失敗しました。もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    onComplete(null);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: 28,
        width: '100%', maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* アイコン */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <User size={28} color="#fff" />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>
            ニックネームを設定しよう
          </h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.7 }}>
            突破プランのDay完了時に<br />
            達成フィードに表示されます
          </p>
        </div>

        {/* サンプル表示 */}
        <div style={{
          background: '#f0f9ff', borderRadius: 12, padding: '12px 14px',
          marginBottom: 20, border: '1px solid #bae6fd',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>
            フィードでの表示イメージ
          </div>
          <div style={{ fontSize: 13, color: '#374151' }}>
            <strong style={{ color: '#6366f1' }}>{nickname.trim() || 'あなたのニックネーム'}</strong>
            さん（🧠 Lタイプ）がDay3を突破！ ⭐ 5
          </div>
        </div>

        {/* 入力 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={nickname}
              onChange={e => { setNickname(e.target.value); setError(''); }}
              placeholder="例：とみさん、学習太郎、匿名希望"
              maxLength={12}
              style={{
                width: '100%', border: `1.5px solid ${error ? '#ef4444' : '#e5e7eb'}`,
                borderRadius: 10, padding: '11px 14px',
                fontSize: 15, outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            {error
              ? <div style={{ fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertCircle size={12} /> {error}
                </div>
              : <div style={{ fontSize: 12, color: '#9ca3af' }}>2〜12文字・後から変更可能</div>
            }
            <div style={{ fontSize: 12, color: nickname.length > 10 ? '#f59e0b' : '#9ca3af' }}>
              {nickname.length}/12
            </div>
          </div>
        </div>

        {/* NG注意 */}
        <div style={{
          background: '#fef9c3', borderRadius: 8, padding: '8px 12px',
          marginBottom: 16, fontSize: 11, color: '#92400e', lineHeight: 1.6,
        }}>
          ⚠️ 不適切な言葉・他者を傷つける表現は使用できません
        </div>

        {/* ボタン */}
        <button
          onClick={handleSave}
          disabled={saving || !nickname.trim()}
          style={{
            width: '100%',
            background: nickname.trim() ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#e5e7eb',
            color: nickname.trim() ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 12, padding: '13px',
            fontSize: 15, fontWeight: 700, cursor: nickname.trim() ? 'pointer' : 'not-allowed',
            marginBottom: 10,
          }}
        >
          {saving ? '保存中...' : 'このニックネームで始める'}
        </button>
        <button
          onClick={handleSkip}
          style={{
            width: '100%', background: 'none', border: 'none',
            color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: '6px',
          }}
        >
          今はスキップ（匿名で参加）
        </button>
      </div>
    </div>
  );
};

export default NicknameSetupModal;
